
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useScrollToSection } from '@/utils/ScrollToSection';

const ScrollToTop = ({ children }: { children: React.ReactNode }) => {
  useScrollToSection();
  return <>{children}</>;
};

import { AuthProvider } from '@/context/AuthContext';
import Index from '@/pages/Index';
import News from '@/pages/News';
import NewsPost from '@/pages/NewsPost';
import Speakers from '@/pages/Speakers';
import Schedule from '@/pages/Schedule';
import SessionDetail from '@/pages/SessionDetail';
import Venue from '@/pages/Venue';
import Tickets from '@/pages/Tickets';
import Contact from '@/pages/Contact';
import BookStand from '@/pages/BookStand';
import Auth from '@/pages/Auth';
import Admin from '@/pages/Admin';
import CreateConference from '@/pages/CreateConference';
import NotFound from '@/pages/NotFound';
import Terms from '@/pages/Terms';
import Privacy from '@/pages/Privacy';
import CodeOfConduct from '@/pages/CodeOfConduct';
import AdminDashboard from '@/pages/AdminDashboard';
import SpeakerDetail from '@/pages/SpeakerDetail';
import AboutUs from '@/pages/AboutUs';
import NewsCategory from '@/pages/NewsCategory';
import AutoNewsPost from '@/pages/AutoNewsPost';


// This component handles redirecting from old to new speaker URLs
const SpeakerRedirect = () => {
  const location = useLocation();
  const newPath = `/speakers${location.pathname.substring(8)}`;
  return <Navigate to={newPath} replace />;
};

// Redirect legacy /news/:slug → /:slug (top-level article URLs)
const NewsSlugRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/news\//, "/");
  return <Navigate to={newPath} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsSlugRedirect />} />
            <Route path="/blog" element={<Navigate to="/news" replace />} />
            <Route path="/category/news" element={<Navigate to="/news" replace />} />
            <Route path="/category/news/" element={<Navigate to="/news" replace />} />
            
            {/* News posts with and without trailing slash */}
            <Route path="/ai-voice-takes-the-stage-global-startups-leading-the-ai-revolution/" element={<NewsPost />} />
            <Route path="/ai-voice-takes-the-stage-global-startups-leading-the-ai-revolution" element={<Navigate to="/ai-voice-takes-the-stage-global-startups-leading-the-ai-revolution/" replace />} />
            
            <Route path="/top-10-ai-startups-in-london-uk-europe-in-2024-and-2025/" element={<NewsPost />} />
            <Route path="/top-10-ai-startups-in-london-uk-europe-in-2024-and-2025" element={<Navigate to="/top-10-ai-startups-in-london-uk-europe-in-2024-and-2025/" replace />} />
            
            <Route path="/top-10-generative-ai-courses-online-in-london-uk-and-europe/" element={<NewsPost />} />
            <Route path="/top-10-generative-ai-courses-online-in-london-uk-and-europe" element={<Navigate to="/top-10-generative-ai-courses-online-in-london-uk-and-europe/" replace />} />
            
            <Route path="/list-of-top-10-telecom-conferences-2025-in-london-uk-europe-germany-italy-france-india-china-uae-saudi-turkey-and-usa/" element={<NewsPost />} />
            <Route path="/list-of-top-10-telecom-conferences-2025-in-london-uk-europe-germany-italy-france-india-china-uae-saudi-turkey-and-usa" element={<Navigate to="/list-of-top-10-telecom-conferences-2025-in-london-uk-europe-germany-italy-france-india-china-uae-saudi-turkey-and-usa/" replace />} />
            
            <Route path="/a-review-of-latest-ai-market-size-statistics-by-company-in-2025/" element={<NewsPost />} />
            <Route path="/a-review-of-latest-ai-market-size-statistics-by-company-in-2025" element={<Navigate to="/a-review-of-latest-ai-market-size-statistics-by-company-in-2025/" replace />} />
            
            <Route path="/top-b2b-ai-trends-for-2025-in-london-uk-europe-us-india-china-uae-saudi-and-canada/" element={<NewsPost />} />
            <Route path="/top-b2b-ai-trends-for-2025-in-london-uk-europe-us-india-china-uae-saudi-and-canada" element={<Navigate to="/top-b2b-ai-trends-for-2025-in-london-uk-europe-us-india-china-uae-saudi-and-canada/" replace />} />
            
            <Route path="/top-10-ai-stocks-list-for-2025-in-london-uk-new-york-paris-frankfurt-hong-kong-india-and-singapore/" element={<NewsPost />} />
            <Route path="/top-10-ai-stocks-list-for-2025-in-london-uk-new-york-paris-frankfurt-hong-kong-india-and-singapore" element={<Navigate to="/top-10-ai-stocks-list-for-2025-in-london-uk-new-york-paris-frankfurt-hong-kong-india-and-singapore/" replace />} />
            
            <Route path="/best-machine-learning-conferences-2026-london-uk-europe-usa-singapore-dubai-saudi-arabia-dublin-ireland-silicon-valley/" element={<NewsPost />} />
            <Route path="/best-machine-learning-conferences-2026-london-uk-europe-usa-singapore-dubai-saudi-arabia-dublin-ireland-silicon-valley" element={<Navigate to="/best-machine-learning-conferences-2026-london-uk-europe-usa-singapore-dubai-saudi-arabia-dublin-ireland-silicon-valley/" replace />} />
            {/* Redirect old ML conferences URL to new one */}
            <Route path="/best-machine-learning-conferences-2026/" element={<Navigate to="/best-machine-learning-conferences-2026-london-uk-europe-usa-singapore-dubai-saudi-arabia-dublin-ireland-silicon-valley/" replace />} />
            <Route path="/best-machine-learning-conferences-2026" element={<Navigate to="/best-machine-learning-conferences-2026-london-uk-europe-usa-singapore-dubai-saudi-arabia-dublin-ireland-silicon-valley/" replace />} />
            
            <Route path="/speakers" element={<Speakers />} />
            <Route path="/agenda" element={<Schedule />} />
            <Route path="/agenda/:day/:slug" element={<SessionDetail />} />
            {/* Redirect old session URLs to new topics */}
            <Route path="/agenda/day2/the-race-to-scale-how-optical-networks-are-powering-ais-infrastructure-boom" element={<Navigate to="/agenda/day2/the-impact-of-genai-on-banking-and-financial-services" replace />} />
            <Route path="/agenda/day2/the-race-to-scale-how-optical-networks-are-powering-ais-infrastructure-boom/" element={<Navigate to="/agenda/day2/the-impact-of-genai-on-banking-and-financial-services" replace />} />
            <Route path="/agenda/day1/keynote-i-scaling-ai-with-mlops" element={<Navigate to="/agenda/day1/keynote-i-beyond-compliance-how-good-regulation-makes-ai-better" replace />} />
            <Route path="/agenda/day1/keynote-i-scaling-ai-with-mlops/" element={<Navigate to="/agenda/day1/keynote-i-beyond-compliance-how-good-regulation-makes-ai-better" replace />} />
            <Route path="/agenda/day1/the-businesses-winning-right-now-arent-bigger-theyre-faster" element={<Navigate to="/agenda/day1/ai-is-only-as-good-as-its-context-how-organizations-can-become-ai-native" replace />} />
            <Route path="/agenda/day1/the-businesses-winning-right-now-arent-bigger-theyre-faster/" element={<Navigate to="/agenda/day1/ai-is-only-as-good-as-its-context-how-organizations-can-become-ai-native" replace />} />
            {/* Handle complex nested agenda URLs */}
            <Route path="/agenda/*/*/:slug" element={<SessionDetail />} />
            <Route path="/agenda/*/*/*/:slug" element={<SessionDetail />} />
            <Route path="/agenda/day1" element={<Navigate to="/agenda#day1" replace />} />
            <Route path="/agenda/day2" element={<Navigate to="/agenda#day2" replace />} />
            <Route path="/schedule" element={<Navigate to="/agenda" replace />} />
            <Route path="/venue" element={<Venue />} />
            <Route path="/register" element={<Tickets />} />
            <Route path="/tickets" element={<Navigate to="/register" replace />} />
            <Route path="/attend" element={<Navigate to="/register" replace />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/exhibition" element={<BookStand />} />
            <Route path="/expo" element={<Navigate to="/exhibition" replace />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/create-conference" element={<CreateConference />} />
            <Route path="/admin-dashboard" element={<AdminDashboard />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/code-of-conduct" element={<CodeOfConduct />} />
            <Route path="/speakers/:slug" element={<SpeakerDetail />} />
            {/* Redirect from old speaker URLs to the new ones */}
            <Route path="/speaker/:slug" element={<SpeakerRedirect />} />
            {/* Dynamic news article at top-level: website.com/{article-slug} */}
            <Route path="/:slug" element={<AutoNewsPost />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          
          <Toaster />
        </ScrollToTop>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
